import { ChangeEvent, FC, useState } from "react";
import { useAtomValue } from "jotai";

import {
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { JunkLoadStatusEnum, ServicesType } from "@/domain/models/job";

import {
  useUpdateAllDriverDumpServicesMutation,
  useUpdateServiceMutation,
} from "../data/mutations/serviceMutations";
import {
  useUpdateAllDriverDumpTasksMutation,
  useUpdateTaskMutation,
} from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithTextInput } from "./DriverScreenWithTextInput";

const DriverFillingWeightSurcharge: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [weightSurcharge, setWeightSurcharge] = useState<string | undefined>(
    serviceData?.weightSurcharge ?? undefined,
  );

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const isDumpAndReturnTask = taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  const {
    mutate: updateAllDriverDumpTasks,
    isPending: isUpdatingAllDriverDumpTasks,
    variables: allDriverDumpTasksVariables,
  } = useUpdateAllDriverDumpTasksMutation();
  const {
    mutate: updateAllDriverDumpServices,
    isPending: isUpdatingAllDriverDumpServices,
  } = useUpdateAllDriverDumpServicesMutation();

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables: taskVariables,
  } = useUpdateTaskMutation();
  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  const isJunkJobWithMultipleLoads = Boolean(
    serviceData?.junkLoads?.length ?? 0 > 1,
  );

  const getNextStep = () => {
    switch (true) {
      case isDriverReviewingTask &&
        (isDumpAndReturnTask || isJunkJobWithMultipleLoads):
        return TaskDriverStatusEnum.TASK_REVIEW;

      case isDriverReviewingTask && !isDumpAndReturnTask:
        return TaskDriverStatusEnum.DUMP_TASKS_REVIEW;

      default:
        return TaskDriverStatusEnum.TAKING_PHOTO_DUMP_RECEIPT;
    }
  };

  const onNextClick = () => {
    const driverFlowStatus = getNextStep();

    const serviceParams: Partial<ServicesType> = {
      driverFlowStatus,
    };

    // Check if there's a list of junk loads
    const junkLoadInProgressIndex =
      serviceData?.junkLoads?.findIndex(
        junkLoad => junkLoad.status === JunkLoadStatusEnum.IN_PROGRESS,
      ) ?? -1;

    // If there's one in progress, means driver is managing data for that specific load
    if (junkLoadInProgressIndex !== -1 && serviceData?.junkLoads) {
      const updatedJunkLoad = serviceData.junkLoads[junkLoadInProgressIndex];

      updatedJunkLoad.weightSurcharge = weightSurcharge ?? "";

      // Change back to completed status if driver is reviewing the task
      // this one is when driver is reviewing the task and pressed to edit the value
      if (isDriverReviewingTask) {
        updatedJunkLoad.status = JunkLoadStatusEnum.COMPLETED;
      }

      serviceData.junkLoads.splice(junkLoadInProgressIndex, 1, updatedJunkLoad);

      serviceParams.junkLoads = [...serviceData.junkLoads];

      updateService({
        serviceId,
        jobId,
        ...serviceParams,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus,
      });

      return;
    }

    if (weightSurcharge) {
      serviceParams.weightSurcharge = weightSurcharge;
    }

    // If it's a dump and return task, update only the current task and service
    if (isDumpAndReturnTask) {
      updateService({
        serviceId,
        jobId,
        ...serviceParams,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus,
      });

      return;
    }

    //This is the case when driver is driving to dump with multiple tasks
    updateAllDriverDumpTasks({ driverFlowStatus });

    updateAllDriverDumpServices(serviceParams);
  };

  const isLoading =
    isUpdatingAllDriverDumpTasks ||
    isUpdatingAllDriverDumpServices ||
    isUpdatingService ||
    isUpdatingTask;

  const variables = allDriverDumpTasksVariables ?? taskVariables;

  const isNextButtonLoading =
    isLoading && variables?.driverFlowStatus === getNextStep();
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.DRIVING_TO_DUMP;

  const goToPreviousStep = () => {
    updateAllDriverDumpTasks({
      driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
    });

    updateAllDriverDumpServices({
      driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
    });
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWeightSurcharge(event.target.value);
  };

  return (
    <DriverScreenWithTextInput
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      shouldShowTopActionButton={false}
      title="driver_filling_weight_surcharge.title"
      titlei18nOptions={{
        stepNumber: isDumpAndReturnTask ? 4 : 2,
      }}
      inputProps={{
        value: weightSurcharge,
        onChange: handleOnChange,
        inputMode: "numeric",
        placeholder: "driver_filling_weight_surcharge.input_placeholder",
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        previousButtonTitle: "driver_filling_weight_surcharge.back_button",
        onPreviousButtonClick:
          isDriverReviewingTask || isDumpAndReturnTask
            ? undefined
            : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingWeightSurcharge };
